import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Users, Sparkles, Calendar } from "lucide-react";
import { ACTIVITY_TYPES, INTENTS, VIBES, MOCK_VENUES, IMG } from "@/lib/mockData";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { apiInvites } from "@/lib/api";

const TYPE_IMG = {
  coffee: IMG.coffee, food: IMG.restaurant, gym: IMG.gym, run: IMG.run,
  surf: IMG.surf, fish: IMG.fish, pool: IMG.pool, beach: IMG.surf,
  gaming: IMG.gaming, kids: IMG.mums,
};

const CreateActivity = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    type: "coffee",
    title: "",
    description: "",
    address: "",
    time: "",
    max: 4,
    intent: "casual_hang",
    vibe: "chill",
    spontaneous: true,
    venue: null,
    notes: "",
  });

  const submit = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("grabb:friction", { detail: { action: "create" } }));
      return;
    }
    if (!form.title || !form.address || !form.time) {
      toast.error("Title, location and time required");
      return;
    }
    setBusy(true);
    try {
      const time_iso = new Date(form.time).toISOString();
      const payload = {
        type: form.type, title: form.title, description: form.description,
        address: form.address, time_iso, max_participants: form.max,
        intent: form.intent, vibe: form.vibe, spontaneous: form.spontaneous,
        venue_id: form.venue?.id || null,
        deal: form.venue?.deal || null,
        image: TYPE_IMG[form.type] || IMG.coffee,
        distance_km: form.venue?.distance_km || 1.0,
      };
      const created = await apiInvites.create(payload);
      toast.success("Activity posted — group chat opened");
      nav(`/activity/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post activity");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-32" data-testid="create-activity-page">
      <header className="sticky top-0 z-30 bg-[#F8F9FA]/85 backdrop-blur-md border-b-2 border-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="w-10 h-10 brutal-border rounded-full bg-white flex items-center justify-center brutal-shadow-sm" data-testid="create-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="font-heading font-black text-xl">Create activity</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Type */}
        <Section title="Type">
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setForm({ ...form, type: t.id })}
                className={`brutal-border rounded-full px-3 py-2 font-bold text-sm ${
                  form.type === t.id ? "bg-[#FF6A2C] text-white" : "bg-white"
                }`}
                data-testid={`create-type-${t.id}`}
              >
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Title + description */}
        <Section title="What & where">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Morning coffee at Reuben Hills"
            className="brutal-input mb-3"
            data-testid="create-title"
          />
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Add a quick note..."
            rows={3}
            className="brutal-input"
            data-testid="create-description"
          />
          <div className="mt-3 flex items-center gap-2 brutal-border bg-[#F8F9FA] rounded-lg px-3 py-2.5">
            <MapPin className="w-4 h-4" />
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Address or venue name"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="create-address"
            />
          </div>
        </Section>

        {/* Time + capacity */}
        <Section title="When & capacity">
          <div className="grid grid-cols-2 gap-3">
            <div className="brutal-border bg-[#F8F9FA] rounded-lg px-3 py-2.5 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <input
                type="datetime-local"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="create-time"
              />
            </div>
            <div className="brutal-border bg-[#F8F9FA] rounded-lg px-3 py-2.5 flex items-center gap-2">
              <Users className="w-4 h-4" />
              <input
                type="number"
                min={2}
                max={20}
                value={form.max}
                onChange={(e) => setForm({ ...form, max: parseInt(e.target.value) || 2 })}
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="create-max"
              />
              <span className="text-xs text-[#555]">spots</span>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setForm({ ...form, spontaneous: true })}
              className={`flex-1 brutal-border rounded-lg p-3 text-left ${form.spontaneous ? "bg-[#FFCC00]" : "bg-white"}`}
              data-testid="create-spontaneous"
            >
              <Sparkles className="w-4 h-4 mb-1" />
              <div className="font-bold text-sm">Spontaneous</div>
              <div className="text-xs text-[#555]">Going soon, prioritise close-by</div>
            </button>
            <button
              onClick={() => setForm({ ...form, spontaneous: false })}
              className={`flex-1 brutal-border rounded-lg p-3 text-left ${!form.spontaneous ? "bg-[#C8E6C9]" : "bg-white"}`}
              data-testid="create-planned"
            >
              <Calendar className="w-4 h-4 mb-1" />
              <div className="font-bold text-sm">Planned</div>
              <div className="text-xs text-[#555]">Scheduled — match by timing fit</div>
            </button>
          </div>
        </Section>

        {/* Intent */}
        <Section title="Why are you doing this?">
          <div className="space-y-2">
            {INTENTS.map((i) => (
              <button
                key={i.id}
                onClick={() => setForm({ ...form, intent: i.id })}
                className={`w-full text-left brutal-border rounded-lg p-3 flex items-center gap-3 ${
                  form.intent === i.id ? "bg-[#0A0A0A] text-white" : "bg-white"
                }`}
                data-testid={`create-intent-${i.id}`}
              >
                <span className="text-xl">{i.emoji}</span>
                <span className="font-bold text-sm">{i.label}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* Vibe */}
        <Section title="Vibe">
          <div className="grid grid-cols-2 gap-2">
            {VIBES.map((v) => (
              <button
                key={v.id}
                onClick={() => setForm({ ...form, vibe: v.id })}
                className={`brutal-border rounded-lg p-3 text-center font-bold text-sm ${
                  form.vibe === v.id ? "ring-4 ring-[#FF6A2C]/40" : ""
                }`}
                style={{ background: v.color }}
                data-testid={`create-vibe-${v.id}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Venue */}
        <Section title="Linked venue (optional)">
          <div className="space-y-2">
            {MOCK_VENUES.map((v) => (
              <button
                key={v.id}
                onClick={() => setForm({ ...form, venue: form.venue?.id === v.id ? null : v })}
                className={`w-full text-left brutal-border rounded-lg p-3 flex items-center gap-3 ${
                  form.venue?.id === v.id ? "bg-[#FFFBEB]" : "bg-white"
                }`}
                data-testid={`create-venue-${v.id}`}
              >
                <img src={v.image} alt={v.name} className="w-10 h-10 rounded-md object-cover brutal-border" />
                <div>
                  <div className="font-bold text-sm">{v.name}</div>
                  <div className="text-xs text-[#555]">{v.category} • {v.distance_km} km</div>
                </div>
              </button>
            ))}
          </div>
        </Section>
      </main>

      <div className="fixed bottom-16 left-0 right-0 bg-white/95 backdrop-blur-md border-t-2 border-[#0A0A0A] px-4 py-3 z-50">
        <div className="max-w-2xl mx-auto">
          <button onClick={submit} disabled={busy} className="w-full brutal-btn bg-[#FF6A2C] text-white py-3.5 disabled:opacity-60" data-testid="create-submit">
            {busy ? "Posting…" : "Post activity"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="label-mono mb-2">{title}</h3>
    <div className="brutal-card p-4">{children}</div>
  </div>
);

export default CreateActivity;
